<template>
  <div :key="refreshKey" class="st-data-table-outer" ref="tableOuter" id="tableOuter" :style="{ '--actions-col-width': actionsColWidth }">
    <div class="st-data-table-scroll-wrapper" ref="scrollWrapper" :style="{ height: tableMaxHeight }">
    <b-table
      :items="stItems"
      :fields="fields"
      :sort-by.sync="stSortBy"
      :sort-desc.sync="stSortDesc"
      :busy="loading"
      :responsive="responsive"
      :hover="hover"
      :per-page="rowsPerPage"
      :current-page="currentPage"
      :filter="filter"
      :head-variant="headVariant"
      :table-variant="tableVariant"
      :thead-class="headClass"
      thead-tr-class="st-data-table-head-row"
      tbody-class="st-data-table-body"
      :tbody-tr-class="rowClass"
      :class="tableClass"
      show-empty
      :selectable="selectable"
      :select-mode="selectMode"
      :actionsClass="actionsClass"
      @row-selected="onRowSelected"
      :no-select-on-click="noSelectOnClick"
      @row-clicked="onRowClicked"
      @sort-changed="sortingChanged"
      :no-local-sorting="serverSideSorting"
      :fixed="fixed"
      sort-icon-left
      ref="selectableTable"
      selected-variant=""
      v-columns-resizable="resizableDirectiveValue"
    >
      <template #empty>
        <div class="st-table-empty-message">{{ emptyText }}</div>
      </template>
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong class="ml-3">{{ $t("GENERAL.LOADING") }}</strong>
        </div>
      </template>
      <template #table-colgroup="scope">
        <col
          v-for="field in scope.fields"
          :key="field.key"
          :style="{ width: getColWidth(field) }"
        />
      </template>
      <template #head()="data">
        <span v-if="data.column === 'selected' && allowSelectAll">
          <input type="checkbox" name="selected" v-model="selectAll" :disabled="disableSelection" />
        </span>
        <span class="st-header-text" v-else>{{ data.label.toUpperCase() }}</span>
      </template>

      <template #cell(Icons) v-if="hasIconDefined()">
        <span class="st-data-table-icon svg-icon svg-icon-md">
          <span v-if="firstColumnIcons.customIcon" v-html="firstColumnIcons.name"></span>
          <i
            v-else
            :class="`text-${firstColumnIcons.type} fas fa-${firstColumnIcons.name}`"
          ></i>
        </span>
      </template>
      <template #cell(selected)="row" v-if="hasSelectedColumnDefined">
        <input
          type="checkbox"
          v-model="row.item.selected"
          v-if="showCheckbox(row.item)"
          :disabled="row.item.locked || disableSelection"
          name="selected"
          @change="checkRow(row)"
        />
      </template>
      <template #cell()="data">
        <span
          v-if="!!data.field.booleanType"
          class="label custom-label"
          :class="{
            'label-success': data.value,
            'label-text-muted': !data.value,
          }"
        >
          <i v-if="data.value" class="fas fa-check text-light"></i>
          <i v-else class="fas fa-check text-light"></i>
        </span>
        <span v-else-if="data.field.labelType && data.value" class="text-truncate">
          <span v-for="(item, i) in data.field.options" :key="i">
            <span v-if="item.name === data.value">
              <span
                v-if="!!item.text"
                :class="`label label-xl label-inline label-light-${item.type}`"
              >
                {{ item.text }}
              </span>
              <span
                v-else
                :class="`label label-xl label-inline label-light-${item.type}`"
              >
                {{ data.value }}
              </span>
            </span>
          </span>
        </span>
        <span v-else-if="data.field.labelType && !data.value" class="text-truncate">
          <span class="label label-xl label-inline label-light-dark"> - </span>
        </span>
        <span v-else-if="data.field.linkable">
          <b-link @click.self.prevent="goToLink(data)">{{ data.value }}</b-link>
        </span>
        <span v-else-if="data.field.password">
          <span class="d-block text-truncate">{{
            ``.padStart(data.value.length, "*")
          }}</span>
        </span>
        <span v-else-if="data.field.customField">
          <component
            :is="data.field.portalTarget"
            :presenter="presenter(data)"
            :dataObj="data"
          />
        </span>
        <div v-else>
          <span
            :class="['d-block text-truncate', { 'font-weight-bold': !!data.field.bold }]"
            :title="cellTitle(data)"
          >
            {{ presenter(data) }}
          </span>
        </div>
      </template>
      <template #cell(Actions)="data" v-if="actionsList && actionsList.length > 0">
        <div class="st-actions-cell">
          <span v-for="(action, i) in actionsList" v-bind:key="i">
            <span v-if="action.displayed">
              <span v-if="!(action.hideOnRow && data.item.hideAction)">
                <b-button
                  v-if="!!action.buttonType"
                  size="sm"
                  @click="doAction(action.name, data)"
                  variant="primary"
                  class="my-1"
                  >{{ action.buttonText }}
                </b-button>
                <b-button
                  v-else
                  :class="`btn btn-icon btn-light btn-hover-${action.type} st-btn-data-table my-1`"
                  @click="doAction(action, data)"
                  v-b-tooltip.hover="{ variant: 'info', container: '#tableOuter', boundary: 'viewport' }"
                  :title="action.tooltipText"
                  :disabled="isActionDisabled(action, data.item)"
                >
                  <span
                    :class="`st-data-table-icon svg-icon svg-icon-md  svg-icon-${action.type}`"
                  >
                    <span v-if="action.customIcon" v-html="action.icon"></span>
                    <i v-else :class="`fas fa-${action.icon}`"></i>
                  </span>
                  <span v-if="action.tooltipText" class="sr-only">{{ action.tooltipText }}</span>
                </b-button>
              </span>
              <span v-else-if="data.item.hideActionNames?.indexOf(action.name) !== -1">
                <b-button
                  :class="`btn btn-icon btn-light btn-hover-${action.type} st-btn-data-table my-1`"
                  @click="doAction(action, data)"
                  v-b-tooltip.hover="{ variant: 'info', container: '#tableOuter', boundary: 'viewport' }"
                  :title="action.tooltipText"
                  :disabled="isActionDisabled(action, data.item)"
                >
                  <span
                    :class="`st-data-table-icon svg-icon svg-icon-md  svg-icon-${action.type}`"
                  >
                    <span v-if="action.customIcon" v-html="action.icon"></span>
                    <i v-else :class="`fas fa-${action.icon}`"></i>
                  </span>
                  <span v-if="action.tooltipText" class="sr-only">{{ action.tooltipText }}</span>
                </b-button>
              </span>
            </span>
          </span>
        </div>
      </template>
      <template v-slot:head(Actions)="data">
        <span class="st-header-text">{{ data.label.toUpperCase() }}</span>
        <template v-if="configurableFields && configurableFields.length > 0">
          <b-button
            id="columnSelectorButton"
            pill
            variant="outline"
            size="sm"
            class="ml-2"
            v-b-tooltip.hover="{ variant: 'info', container: '#tableOuter', boundary: 'viewport' }"
            :title="$t('GENERAL.TABLE.COLUMN_SELECTOR_TITLE')"
          >
            <b-icon-list-check font-scale="1.5"></b-icon-list-check>
          </b-button>
          <b-popover
            target="columnSelectorButton"
            triggers="click"
            :show.sync="showColumnSelector"
            placement="bottomleft"
            container="tableOuter"
            boundary="viewport"
            custom-class="st-column-selector-popover"
            ref="popover"
          >
          <template #title>{{ $t('GENERAL.TABLE.COLUMN_SELECTOR_TITLE') }}</template>
          <div class="st-col-config-wrapper">
            <b-form-input
              v-model="columnSelectorSearch"
              :placeholder="$t('GENERAL.DUAL_LIST_BOX.SEARCH_PLACEHOLDER')"
              class="st-col-config-search"
              type="search"
            />
            <div class="st-col-config-list">
            <div class="d-flex flex-row justify-content-between align-items-center st-col-config-item st-col-config-select-all">
              <b-form-checkbox
                :checked="allColumnsSelected"
                :indeterminate="someColumnsSelected"
                @change="toggleSelectAllColumns"
                class="mr-4 st-col-config-checkbox"
              >
                {{ $t('GENERAL.TABLE.SELECT_ALL_COLUMNS') }}
              </b-form-checkbox>
            </div>
            <div class="st-col-config-divider"></div>
            <div
              class="d-flex flex-row justify-content-between align-items-center st-col-config-item"
              v-for="configurableField in filteredConfigurableFields"
              :key="configurableField.key"
            >
              <b-form-checkbox
                :checked="selectedConfigurableFields.includes(configurableField.key)"
                @change="handleColumnSelected($event, configurableField.key)"
                class="mr-4 st-col-config-checkbox"
              >
                {{ configurableField.label }}
              </b-form-checkbox>
              <b-badge
                pill
                variant="primary"
                :style="{ visibility: selectedConfigurableFields.includes(configurableField.key) ? 'visible' : 'hidden' }"
                >{{
                  selectedConfigurableFields.indexOf(configurableField.key) + 1
                }}</b-badge
              >
            </div>
            <div v-if="filteredConfigurableFields.length === 0" class="st-col-config-empty">
              {{ $t('GENERAL.DUAL_LIST_BOX.SEARCH_RESULTS') }}
            </div>
            </div>
            <div class="d-flex flex-column mt-2" style="gap: 0.4rem;">
              <b-button
                @click="applyColumnSelector(false)"
                size="sm"
                variant="primary"
                block
                >{{ $t('GENERAL.TABLE.APPLY') }}</b-button
              >
              <b-button
                v-if="persistanceIdentifier"
                @click="resetColumnWidths"
                size="sm"
                variant="outline-primary"
                block
                >{{ $t('GENERAL.TABLE.RESET_WIDTHS') }}</b-button
              >
              <b-button
                @click="closeColumnSelector"
                size="sm"
                variant="outline-primary"
                block
                >{{ $t('GENERAL.TABLE.CANCEL') }}</b-button
              >
            </div>
          </div>
        </b-popover>
        </template>
      </template>
    </b-table>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import i18n from "@/shared/plugins/vue-i18n";
import { isObjectEmpty } from "@/core/helpers/globalMethods";
import EnableTruncate from "../components/data-table/DataTableTruncateField";
import BeneficiaryCustomField from "../components/data-table/BeneficiaryCustomField";
import CategoryUsageTypeCustomField from "../components/data-table/CategoryUsageTypeCustomField";
import ResponseDaysCustomField from "../components/data-table/ResponseDaysCustomField";
import LocalityCustomField from "../components/data-table/LocalityCustomField";
import HtmlPreviewCustomField from "../components/data-table/HtmlPreviewCustomField";
import LabelHintCustomField from "../components/data-table/LabelHintCustomField";
import {
  BSpinner,
  VBTooltip,
  BIconListCheck,
  BPopover,
  BFormCheckbox,
  BFormInput,
  BBadge,
} from "bootstrap-vue";
import Message from "@/shared/message/message";

function addClass(existing, next) {
  if (!existing) return next;
  const tokens = existing.split(/\s+/).filter(Boolean);
  for (const t of next.split(/\s+/).filter(Boolean)) {
    if (!tokens.includes(t)) tokens.push(t);
  }
  return tokens.join(" ");
}

export default {
  name: "StDataTable",
  components: {
    "b-spinner": BSpinner,
    EnableTruncate,
    BeneficiaryCustomField,
    CategoryUsageTypeCustomField,
    LocalityCustomField,
    BIconListCheck,
    BPopover,
    BFormCheckbox,
    BFormInput,
    BBadge,
    ResponseDaysCustomField,
    HtmlPreviewCustomField,
    LabelHintCustomField,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    configurableFields: {
      type: Array,
      required: false,
    },
    selectionWarningThreshold: {
      type: Number,
      default: null,
    },
    selectionLimit: {
      type: Number,
      default: null,
    },
    sortBy: {
      type: String,
      default: "",
    },
    sortDesc: {
      type: Boolean,
      default: false,
    },
    responsive: {
      type: [String, Boolean],
      // .st-data-table-scroll-wrapper does the scrolling: true nests a second scroller, false breaks v-columns-resizable
      default: "sm",
    },
    hover: {
      type: Boolean,
      default: false,
    },
    perPage: {
      type: Number,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    filter: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    actions: {
      type: Array,
      default: () => [],
    },
    headVariant: {
      type: String,
      default: "light",
    },
    tableVariant: {
      type: String,
      default: "light",
    },
    actionsLabel: {
      type: String,
      default: i18n.t("GENERAL.TABLE.ACTIONS"),
    },
    modelPresenter: {
      type: Function,
    },
    selectMode: {
      type: String,
      default: "single", // other options: multi, range
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    firstColumnIcons: {
      type: Object,
      default: () => ({}),
    },
    headClass: {
      type: String,
      default: "st-data-table-head",
    },
    actionsClass: {
      type: String,
    },
    tbodyTrClass: {
      type: String,
      default: "st-data-table-body-row",
    },
    noSelectOnClick: {
      type: Boolean,
      default: false,
    },
    treeView: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: i18n.t("GENERAL.TABLE_EMPTY_TEXT"),
    },
    firstColumnSelectable: {
      type: Boolean,
      default: false,
    },
    stateModule: String,
    serverSideSorting: {
      type: Boolean,
      default: false,
    },
    customClass: {
      type: String,
      default: "",
    },
    allowSelectAll: {
      type: Boolean,
      default: false,
    },
    customBooleanTypeField: {
      type: Boolean,
      default: false,
    },
    checkLimit: {
      type: Number,
    },
    checkLimitMessage: {
      type: String,
    },
    persistanceIdentifier: {
      type: [String, Object],
    },
    fixed: {
      type: Boolean,
      default: true
    },
    noMaxHeight: {
      type: Boolean,
      default: false,
    },
    disableSelection: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showColumnSelector: false,
      stFields: [],
      tableMaxHeight: null,
      actionsList: this.actions.map((action) => {
        return {
          ...action,
          displayed: typeof action.permission === "boolean" ? action.permission : true,
        };
      }),
      selectAll: false,
      snapshotSelectedConfigurableFields: [],
      selectedConfigurableFields: [],
      columnSelectorSearch: "",
      hasWarnedAboutSelectionCount: false,
      refreshKey: 0
    };
  },
  computed: {
    ...mapGetters({
      appConfig: "shared/appConfig",
      currentUser: "auth/currentUser",
    }),
    persistanceKey() {
      if (!this.persistanceIdentifier) return null;
      if (typeof this.persistanceIdentifier === 'object') return this.persistanceIdentifier.key;
      return `${this.persistanceIdentifier}_${this.currentUser.id}`;
    },
    resizableDirectiveValue() {
      if (!this.persistanceIdentifier) return null;
      if (typeof this.persistanceIdentifier === 'object') return this.persistanceIdentifier;
      return `${this.persistanceKey}_resize`;
    },
    rowsPerPage() {
      return this.perPage || this.appConfig.RECORDS_PER_PAGE;
    },
    hasSelectedColumnDefined() {
      return this.firstColumnSelectable;
    },
    allColumnsSelected() {
      const selectableFields = this.selectionLimit
        ? this.configurableFields.slice(0, this.selectionLimit)
        : this.configurableFields;
      return (
        selectableFields.length > 0 &&
        selectableFields.every((f) =>
          this.selectedConfigurableFields.includes(f.key)
        )
      );
    },
    someColumnsSelected() {
      return (
        !this.allColumnsSelected &&
        this.configurableFields.some((f) =>
          this.selectedConfigurableFields.includes(f.key)
        )
      );
    },
    filteredConfigurableFields() {
      const search = this.columnSelectorSearch.trim().toLocaleLowerCase();
      if (!search) return this.configurableFields;

      return this.configurableFields.filter((field) =>
        String(field.label || field.key).toLocaleLowerCase().includes(search)
      );
    },
    storedSortData() {
      return this.stateModule ? this.$store.getters[`${this.stateModule}/sorting`] : {};
    },
    stSortBy: {
      get() {
        return this.storedSortData?.sortBy ?? this.sortBy;
      },
      set(val) {
        this.$emit("sortBy", val);
      },
    },
    stSortDesc: {
      get() {
        return this.storedSortData?.sortDesc ?? this.sortDesc;
      },
      set(val) {
        this.$emit("sortDesc", val);
      },
    },
    stItems() {
      if (this.hasSelectedColumnDefined) {
        return this.items.map((item) => ({ ...item, selected: false }));
      }

      return this.items;
    },
    tableClass() {
      let cssClass = "st-data-table";
      if (this.customClass) {
        cssClass += ` ${this.customClass}`;
      }
      return cssClass;
    },
    actionsColWidth() {
      const PER_ACTION = 40;

      const actions = this.actionsList || [];
      const items = this.items || [];

      const isRendered = (action, item) => {
        if (!action.displayed) return false;
        if (!action.hideOnRow) return true;
        if (!item?.hideAction) return true;
        return item?.hideActionNames?.indexOf(action.name) !== -1;
      };

      const maxPerRow = items.length
        ? items.reduce(
            (max, item) => Math.max(max, actions.filter((a) => isRendered(a, item)).length),
            0
          )
        : actions.filter((a) => a.displayed).length;

      return `${Math.max(88, maxPerRow * PER_ACTION + 8)}px`;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateMaxHeight();
    });
    window.addEventListener('resize', this.calculateMaxHeight);
    document.addEventListener('click', this.handleClickOutside);
  },
  watch: {
    refreshKey() {
      this.$nextTick(() => {
        this.calculateMaxHeight();
      });
    },
    items() {
      this.$nextTick(() => {
        this.calculateMaxHeight();
      });
    },
    selectAll(val) {
      if (val) {
        this.stItems.forEach((item) => {
          if (!item.locked) item.selected = true;
        });

        const selectableRows = this.selectableRows(this.stItems);
        if (this.checkLimit && selectableRows.length > this.checkLimit) {
          Message.error(this.checkLimitMessage, { number: this.checkLimit });
        }
      } else {
        const selectedRows = this.selectedRows(this.stItems);
        const lockedRows = this.lockedRows(this.stItems);

        if (selectedRows.length + lockedRows.length === this.items.length) {
          this.stItems.forEach((item) => {
            item.selected = false;
          });
        }
      }
      this.$emit(
        "itemChecked",
        this.checkLimit
          ? this.selectableRows(this.stItems)
          : this.selectedRows(this.stItems)
      );
    },
  },
  created() {
    this.manageActionsColumn();
    if (this.configurableFields && this.configurableFields.length > 0) {
      if (this.persistanceKey) {
        const persistedFields = JSON.parse(
          localStorage.getItem(this.persistanceKey) ||
            "[]"
        );
        if (persistedFields && persistedFields.length > 0) {
          const currentFieldKeys = this.configurableFields.map((field) => field.key);
          const selectedExistingFields = persistedFields.filter((key) =>
            currentFieldKeys.includes(key)
          );

          this.snapshotSelectedConfigurableFields = this.normalizeConfigurableSelection(selectedExistingFields);
          this.selectedConfigurableFields = [...this.snapshotSelectedConfigurableFields];
          this.applyColumnSelector(true);
        } else {
          this.populateSelectedConfigurableFieldsSnapshot();
        }
      } else {
        this.populateSelectedConfigurableFieldsSnapshot();
      }
      this.$emit("columnsSelected", this.selectedConfigurableFields);
    }
  },
  methods: {
    ...mapActions({
      setSortingParams: "shared/fetchSortingParams",
    }),
    doAction(action, data) {
      if (action.name !== "delete") {
        this.$emit(`${action.name}`, data);
      } else {
        // customDeleteMsg can be defined inside action->delete object
        this.$alert({
          type: "warning",
          text: !!action.customDeleteMsg
            ? action.customDeleteMsg
            : this.$t("GENERAL.ACTIONS.DELETE_MESSAGE"),
          confirmButtonText: this.$t("GENERAL.YES"),
          cancelButtonText: this.$t("GENERAL.NO"),
          hasConfirmation: true,
          confirmCallback: () => {
            this.$emit(`${action.name}`, data);
          },
        });
      }
    },
    manageActionsColumn() {
      this.stFields = this.fields;

      const hasConfigurableFields = this.configurableFields && this.configurableFields.length > 0;
      const tdClass = this.actionsClass
        ? `${this.actionsClass} st-actions-column`
        : "st-actions-column";
      const existing = this.stFields.find((field) => field.key === "Actions");

      if (existing) {
        existing.thClass = addClass(existing.thClass, "st-actions-column");
        existing.tdClass = addClass(existing.tdClass, tdClass);
      } else if (this.actionsList.length || hasConfigurableFields) {
        this.stFields.push({
          key: "Actions",
          label: this.actionsLabel,
          thClass: "st-actions-column",
          tdClass,
        });
      }
      if (this.hasIconDefined()) {
        this.stFields.unshift({ key: "Icons", label: "" });
      }
    },
    goToLink(data) {
      this.$emit("goToLink", data);
    },
    presenter(data) {
      if (!this?.modelPresenter) return data.value;
      return this.modelPresenter(data.item, data.field.name);
    },
    cellTitle(data) {
      const value = this.presenter(data);
      return value === null || value === undefined ? '' : String(value);
    },
    onRowSelected(items) {
      this.$emit("onRowSelected", items);
    },
    hasIconDefined() {
      return !isObjectEmpty(this.firstColumnIcons);
    },
    onRowClicked(item, index) {
      this.$emit("onRowClicked", { item, index });
    },
    rowClass(item, type) {
      let classes = this.tbodyTrClass;
      // TODO: Abstract
      if (item?.is_extension) {
        classes += " is-child-row";
      }

      if (this.hasSelectedColumnDefined) {
        classes += " selectable";
      }

      return classes;
    },
    checkRow(data) {
      this.$set(this.stItems, data.index, data.item);
      const selectedRows = this.selectedRows(this.stItems);
      const selectableRows = this.selectableRows(this.stItems);
      if (this.checkLimit && selectableRows.length > this.checkLimit) {
        Message.error(this.checkLimitMessage, { number: this.checkLimit });
      }

      if (selectedRows.length < this.stItems.length) {
        this.selectAll = false;
      }
      if (selectedRows.length === this.stItems.length) {
        this.selectAll = true;
      }

      this.$emit("itemChecked", this.checkLimit ? selectableRows : selectedRows);
    },
    selectedRows(data) {
      return data.filter((el) => el.selected && !el.locked);
    },
    lockedRows(data) {
      return data.filter((el) => !el.selected && el.locked);
    },
    selectableRows(data) {
      return data.filter((el) => el.selected && !el.hideSelectable);
    },
    sortingChanged(ctx) {
      if (this.serverSideSorting) {
        const sortingParams = { sortBy: ctx.sortBy, sortDesc: ctx.sortDesc };
        this.setSortingParams({ stateModule: this.stateModule, sortingParams });
      }
    },
    deselectAll(value) {
      if (this.hasSelectedColumnDefined && value) {
        this.selectAll = false;
      }
    },
    showCheckbox(item) {
      return !item?.hideSelectable;
    },
    getColWidth(field) {
      if (field.key === "Icons") {
        return "80px";
      }
      if (field.booleanType && this.customBooleanTypeField) {
        return "80px";
      }
      if (field.doubleSize) {
        return "360px";
      }
      return "";
    },
    handleClickOutside(e) {
      if (!this.showColumnSelector) return;
      const popoverEl = this.$refs.tableOuter?.querySelector('.st-column-selector-popover');
      const buttonEl = document.getElementById('columnSelectorButton');
      if (
        popoverEl && !popoverEl.contains(e.target) &&
        buttonEl && !buttonEl.contains(e.target)
      ) {
        this.closeColumnSelector();
      }
    },
    closeColumnSelector() {
      this.selectedConfigurableFields = [...this.snapshotSelectedConfigurableFields];
      this.showColumnSelector = false;
    },
    resetColumnWidths() {
      localStorage.removeItem(`${this.persistanceKey}_resize`);
      this.refreshKey++;
      this.showColumnSelector = false;
    },
    applyColumnSelector(initialLoad) {
      if (this.fields.find((f) => f.key === "Actions")) {
        this.fields.splice(0, this.fields.length - 1);
      } else {
        this.fields.splice(0, this.fields.length);
      }
      this.selectedConfigurableFields.reverse().forEach((key) => {
        const field = this.configurableFields.find((c) => c.key === key);
        if (field) {
          this.fields.unshift(field);
        }
      });
      if (this.allowSelectAll) {
        this.fields.unshift({label: '', key: 'selected'})
      }
      this.selectedConfigurableFields = this.selectedConfigurableFields.reverse();
      if (this.persistanceKey) {
        localStorage.setItem(
          this.persistanceKey,
          JSON.stringify(this.selectedConfigurableFields)
        );
      }
      if (this.persistanceKey && !initialLoad) {
        localStorage.removeItem(
          `${this.persistanceKey}_resize`
        );
        this.refreshKey++;
      }
      this.showColumnSelector = false;
      this.$emit("columnsSelected", this.selectedConfigurableFields);
    },
    toggleSelectAllColumns() {
      if (this.allColumnsSelected) {
        this.setConfigurableSelection([]);
      } else {
        const unselected = this.configurableFields
          .map((f) => f.key)
          .filter((k) => !this.selectedConfigurableFields.includes(k));
        this.setConfigurableSelection([...this.selectedConfigurableFields, ...unselected]);
      }
    },
    handleColumnSelected(checked, key) {
      const selection = [...this.selectedConfigurableFields];
      if (checked) {
        selection.push(key);
      } else {
        selection.splice(selection.indexOf(key), 1);
      }
      this.setConfigurableSelection(selection);
    },
    normalizeConfigurableSelection(keys) {
      const currentKeys = new Set(this.configurableFields.map((field) => field.key));
      const uniqueKeys = [...new Set(keys)].filter((key) => currentKeys.has(key));

      if (!this.selectionLimit || uniqueKeys.length <= this.selectionLimit) {
        return uniqueKeys;
      }

      this.$alert({
        type: "warning",
        title: this.$t("GENERAL.TABLE.MAX_SELECTION_TITLE"),
        text: this.$t("GENERAL.TABLE.MAX_SELECTION_TEXT", { max: this.selectionLimit }),
        showConfirmButton: false,
      });
      const selectedKeys = new Set(uniqueKeys);
      return this.configurableFields
        .map((field) => field.key)
        .filter((key) => selectedKeys.has(key))
        .slice(0, this.selectionLimit);
    },
    setConfigurableSelection(keys) {
      const exceededLimit = this.selectionLimit && keys.length > this.selectionLimit;
      this.selectedConfigurableFields = this.normalizeConfigurableSelection(keys);

      if (exceededLimit) {
        this.hasWarnedAboutSelectionCount = true;
      } else if (
        this.selectionWarningThreshold
        && this.selectedConfigurableFields.length > this.selectionWarningThreshold
        && !this.hasWarnedAboutSelectionCount
      ) {
        this.$alert({
          type: "warning",
          title: this.$t("GENERAL.TABLE.RECOMMENDED_SELECTION_TITLE"),
          text: this.$t("GENERAL.TABLE.RECOMMENDED_SELECTION_TEXT", {
            selected: this.selectedConfigurableFields.length,
            recommended: this.selectionWarningThreshold,
            max: this.selectionLimit,
          }),
          showConfirmButton: false,
        });
        this.hasWarnedAboutSelectionCount = true;
      }

      if (!this.selectionWarningThreshold || this.selectedConfigurableFields.length <= this.selectionWarningThreshold) {
        this.hasWarnedAboutSelectionCount = false;
      }
    },
    populateSelectedConfigurableFieldsSnapshot() {
      const initialSelection = this.fields
        .filter((f) => f.key !== "Actions" && f.key !== "selected")
        .map((field) => field.key);
      this.snapshotSelectedConfigurableFields = this.normalizeConfigurableSelection(initialSelection);
      this.selectedConfigurableFields = [...this.snapshotSelectedConfigurableFields];
      if (this.selectedConfigurableFields.length < initialSelection.length) {
        this.applyColumnSelector(true);
      }
    },
    calculateMaxHeight() {
      if (this.noMaxHeight) return;
      const wrapper = this.$refs.scrollWrapper;
      if (!wrapper) return;
      const top = wrapper.getBoundingClientRect().top;
      const paginationEl = wrapper.closest('.st-page-body')?.querySelector('.st-pagination');
      const bottomOffset = paginationEl ? paginationEl.offsetHeight + 16 : 40;
      this.tableMaxHeight = `${window.innerHeight - top - bottomOffset}px`;
    },
    isActionDisabled(action, item) {
      // Check if action has a global disabled flag
      if (action.disabled) {
        return true;
      }

      // Check if action has a disabledCondition function
      if (typeof action.disabledCondition === 'function') {
        return action.disabledCondition(item);
      }

      return false;
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateMaxHeight);
    document.removeEventListener('click', this.handleClickOutside);
    // clear sorting fields (if exists)
    this.fields.forEach((field) => {
      if (field.sortable) field.sortable = false;
    });
  },
};
</script>
